import * as RadixAccordion from '@radix-ui/react-accordion'
import { useState, forwardRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

interface AccordionItem {
  title: string
  subtitle?: string
  content: React.ReactNode
  defaultOpen?: boolean
  open?: boolean
  onOpen?: (open: boolean) => void
}

interface AccordionProps {
  items: AccordionItem[]
}

// Fix: motion.div with forwardRef for Radix asChild compatibility
const MotionDiv = forwardRef<HTMLDivElement, React.ComponentProps<typeof motion.div>>(
  (props, ref) => <motion.div ref={ref} {...props} />,
)
MotionDiv.displayName = 'MotionDiv'

export const Accordion: React.FC<AccordionProps> = ({ items }) => {
  // Track open state for all items (uncontrolled)
  const [openStates, setOpenStates] = useState(items.map((item) => !!item.defaultOpen))

  const handleValueChange = (idx: number | undefined) => {
    const nextOpen = idx !== undefined && openStates[idx] === false
    setOpenStates((states) => states.map((open, i) => (i === idx ? nextOpen : false)))
    if (idx !== undefined && idx >= 0) {
      items[idx].onOpen?.(nextOpen)
    }
  }

  return (
    <RadixAccordion.Root
      type="single"
      collapsible
      value={
        openStates.findIndex(Boolean) !== -1
          ? `item-${openStates.findIndex(Boolean)}`
          : 'undefined-item'
      }
      onValueChange={(val) => {
        const idx =
          typeof val === 'string' && val.startsWith('item-') ? Number(val.split('-')[1]) : -1

        handleValueChange(idx)
      }}
      className="w-full border-0 border-b-1 border-solid border-brand-navy"
    >
      {items.map((item, idx) => {
        const currentOpen = openStates[idx]
        return (
          <RadixAccordion.Item value={`item-${idx}`} key={idx}>
            <RadixAccordion.Header>
              <RadixAccordion.Trigger className="w-full flex items-center justify-between py-4 px-0 focus:outline-none bg-transparent border-0 border-t-1 border-brand-navy border-solid text-brand-navy cursor-pointer hover:text-brand-blue transition-colors">
                <span className="text-left">
                  <span className="font-bold text-base text-brand-blue">{item.title}</span>
                  {item.subtitle && (
                    <span className="font-normal text-sm italic text-brand-navy/70 ml-2">
                      {item.subtitle}
                    </span>
                  )}
                </span>
                <span className="transition-transform duration-200 radix-state-open:rotate-45">
                  <svg width="32" height="32" viewBox="0 0 18 18" aria-hidden="true">
                    <line
                      x1="4"
                      y1="9"
                      x2="14"
                      y2="9"
                      stroke="#0b2948"
                      strokeWidth="0.5"
                      strokeLinecap="round"
                    />
                    {!currentOpen && (
                      <line
                        x1="9"
                        y1="4"
                        x2="9"
                        y2="14"
                        stroke="#0b2948"
                        strokeWidth="0.5"
                        strokeLinecap="round"
                      />
                    )}
                  </svg>
                </span>
              </RadixAccordion.Trigger>
            </RadixAccordion.Header>
            <AnimatePresence initial={false}>
              {currentOpen && (
                <RadixAccordion.Content asChild forceMount>
                  <MotionDiv
                    key={`accordion-content-${idx}`}
                    initial={{ gridTemplateRows: '0fr', opacity: 0, paddingBottom: 0 }}
                    animate={{ gridTemplateRows: '1fr', opacity: 1, paddingBottom: '32px' }}
                    exit={{ gridTemplateRows: '0fr', opacity: 0, paddingBottom: 0 }}
                    transition={{ duration: 0.3, ease: [0.87, 0, 0.13, 1] }}
                    className="grid overflow-hidden px-0"
                    style={{ gridTemplateRows: '1fr' }}
                  >
                    <div className="min-h-0 overflow-hidden [&>p:last-child]:font-bold [&>p:last-child]:text-brand-blue">
                      {item.content}
                    </div>
                  </MotionDiv>
                </RadixAccordion.Content>
              )}
            </AnimatePresence>
          </RadixAccordion.Item>
        )
      })}
    </RadixAccordion.Root>
  )
}

export default Accordion
